import {
  Button,
  FormHelperText,
  IconButton,
  Stack,
  TextField
} from '@mui/material'
import { useFormikContext } from 'formik'
import React from 'react'

import Icon from 'components/Icon'
import {
  TeamExternalIntegration,
  StatusCheckHeader,
  authenticationStatus
} from 'types/api/services/golden-signal'

const StatusCheckHeaders: React.FC<{
  formPrefix?: string
  isCustom: boolean
  isEditingDisabled?: boolean
}> = ({ formPrefix, isCustom, isEditingDisabled = false }) => {
  const form = useFormikContext<TeamExternalIntegration>()

  const values = formPrefix ? form.values?.[formPrefix] : form.values
  const touched = formPrefix ? form.touched?.[formPrefix] : form.touched
  const errors = formPrefix ? form.errors?.[formPrefix] : form.errors
  const headers = `${formPrefix ? `${formPrefix}.` : ''}headers`

  if (!values || !values.headers) {
    return null
  }

  const handleChange = (e) => {
    form.setFieldValue(
      'integration.lastAuthenticationStatus',
      authenticationStatus.UnAuthenticated
    )
    form.handleChange(e)
  }

  return (
    <Stack alignItems="flex-start" spacing={2} sx={{ width: '100%' }}>
      {typeof errors?.headers === 'string' && errors?.headers && (
        <FormHelperText
          data-testid="team-integration-headers-error"
          error={true}
        >
          {errors.headers}
        </FormHelperText>
      )}
      {values?.headers?.map((h, index) => (
        <Stack
          key={h.name || index}
          data-testid="team-integration-header"
          direction="row"
          spacing={2}
          sx={{ width: '100%' }}
        >
          {isCustom && (
            <TextField
              data-testid={`team-integration-header-${index}-key`}
              disabled={isEditingDisabled}
              error={
                touched?.headers?.[index]?.key &&
                (Boolean(
                  (errors?.headers?.[index] as StatusCheckHeader)?.key
                ) ||
                  Boolean(
                    typeof errors?.headers === 'string' && errors?.headers
                  ))
              }
              helperText={
                touched?.headers?.[index]?.key &&
                (errors?.headers?.[index] as StatusCheckHeader)?.key
              }
              label="Name"
              name={`${headers}[${index}].key`}
              type="text"
              value={values?.headers?.[index]?.key}
              onBlur={form.handleBlur}
              onChange={handleChange}
            />
          )}
          <TextField
            data-testid={`team-integration-header-${index}-value`}
            disabled={isEditingDisabled}
            error={
              touched?.headers?.[index]?.value &&
              Boolean((errors?.headers?.[index] as StatusCheckHeader)?.value)
            }
            fullWidth={!isCustom}
            helperText={
              touched?.headers?.[index]?.value &&
              (errors?.headers?.[index] as StatusCheckHeader)?.value
            }
            inputProps={{
              'data-testid': `team-integration-header-${index}-input-value`
            }}
            label={values.headers?.[index]?.label ?? 'Value'}
            name={`${headers}[${index}].value`}
            type="text"
            value={values.headers[index].value}
            onBlur={form.handleBlur}
            onChange={handleChange}
          />
          {isCustom && !isEditingDisabled && (
            <Stack alignItems="center" alignSelf="center">
              <IconButton
                color="error"
                data-testid={`team-integration-header-${index}-remove`}
                size="large"
                onClick={() =>
                  form.setFieldValue(`${headers}`, [
                    ...values.headers.filter((h, i) => i !== index)
                  ])
                }
              >
                <Icon icon="circleTrash" />
              </IconButton>
            </Stack>
          )}
        </Stack>
      ))}
      {isCustom && !isEditingDisabled && (
        <Button
          color="primary"
          data-testid="team-integration-add-header"
          size="small"
          startIcon={<Icon icon="plus" />}
          variant="contained"
          onClick={() =>
            form.setFieldValue(`${headers}`, [
              ...values.headers,
              { key: '', value: '' }
            ])
          }
        >
          Add Value
        </Button>
      )}{' '}
    </Stack>
  )
}

export default StatusCheckHeaders
